import React from 'react';
import {
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import StatisticsGlassCard from './StatisticsGlassCard';
import StatisticsDesktopSectionHeader from './StatisticsDesktopSectionHeader';
import { STATS_ICONS } from './statisticsAssets';
import useChartContainerReady from './useChartContainerReady';

export default function StatisticsDesktopFinanceFlowCard({
    financeFlowStats,
    isEditMode,
    longPressHandlers,
    theme
}) {
    const { containerRef, isReady } = useChartContainerReady();

    const incomeColor = '#10B981';
    const expenseColor = '#F43F5E';
    const balanceColor = theme?.primaryHex || '#6366F1';

    const chartData = (financeFlowStats || []).map(item => ({
        ...item,
        수입: item.수입,
        지출: -item.지출,
        잔액: item.수입 - item.지출
    }));

    const maxVal = Math.max(1, ...chartData.flatMap(d => [d.수입, Math.abs(d.지출), Math.abs(d.잔액)]));

    const yAxisTickFormatter = (val) => {
        const value = Math.abs(val);
        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
        return value;
    };

    return (
        <StatisticsGlassCard className="h-full p-6 flex flex-col" {...longPressHandlers} isEditMode={isEditMode}>
            <div className="w-full mb-2">
                <StatisticsDesktopSectionHeader
                    iconSrc={STATS_ICONS.chart}
                    title="재정 월별 흐름"
                    badge="6개월"
                />
            </div>

            <div ref={containerRef} className="relative w-full flex-1 min-h-[180px] min-w-0">
                {isReady ? (
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={180}>
                        <ComposedChart
                            data={chartData}
                            margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={incomeColor} stopOpacity={0.9} />
                                    <stop offset="100%" stopColor={incomeColor} stopOpacity={0.5} />
                                </linearGradient>
                                <linearGradient id="expenseGrad" x1="0" y1="1" x2="0" y2="0">
                                    <stop offset="0%" stopColor={expenseColor} stopOpacity={0.9} />
                                    <stop offset="100%" stopColor={expenseColor} stopOpacity={0.5} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                            <ReferenceLine yAxisId="left" y={0} stroke="#E5E7EB" strokeWidth={1.5} />
                            <XAxis
                                dataKey="month"
                                tick={{ fill: '#9CA3AF', fontSize: 10, fontWeight: 'bold' }}
                                axisLine={false}
                                tickLine={false}
                                dy={8}
                                tickFormatter={(val) => val.slice(5)}
                            />
                            <YAxis
                                yAxisId="left"
                                tick={{ fill: '#D1D5DB', fontSize: 10 }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={yAxisTickFormatter}
                                domain={[-(maxVal * 1.1), maxVal * 1.1]}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: 'rgba(255,255,255,0.98)',
                                    border: 'none',
                                    borderRadius: '16px',
                                    boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
                                    padding: '10px 14px',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                }}
                                formatter={(value, name) => [`${Math.abs(value).toLocaleString()}원`, name]}
                                labelStyle={{ color: '#4B5563', marginBottom: '8px', borderBottom: '1px solid #E5E7EB', paddingBottom: '4px' }}
                            />
                            <Legend
                                wrapperStyle={{ fontSize: '11px', fontWeight: 'bold', paddingTop: '10px' }}
                                iconType="circle"
                                iconSize={8}
                            />
                            <Bar yAxisId="left" dataKey="수입" barSize={16} fill="url(#incomeGrad)" radius={[5, 5, 0, 0]} animationDuration={1200} />
                            <Bar yAxisId="left" dataKey="지출" barSize={16} fill="url(#expenseGrad)" radius={[0, 0, 5, 5]} animationDuration={1200} />
                            <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="잔액"
                                stroke={balanceColor}
                                strokeWidth={3}
                                dot={{ r: 4, strokeWidth: 2, fill: '#fff', stroke: balanceColor }}
                                activeDot={{ r: 7, fill: balanceColor, stroke: '#fff', strokeWidth: 3 }}
                                animationDuration={1500}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                ) : null}
            </div>
        </StatisticsGlassCard>
    );
}
